import { Box, CircularProgress, Grid, IconButton, MenuItem, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { ClosableDialog } from 'components/controls/dialogs/ClosableDialog'
import { ExpansionAutocomplete } from 'components/controls/misc/ExpansionAutocomplete'
import { Button } from 'components/facets'
import ImagePopover from 'components/facets/ImagePopover'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { useCardData, useExpansionData } from 'components/providers/CardDataProvider'
import { getMkmExpansionChangelogs, getMkmProductChangelogs, getMkmProducts } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'
import { Expansion, gameMetaData, GAME_NAMES, getGameMetaData, IdGame } from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'
import { Filter } from '../stockPricing/FilterDock/Filter'
import { getFormattedDate } from './BackofficePresenter'

const PAGE_SIZE = 50

// Tall enough for the card image, as in the relisting and Cardtrader review tables
const ROW_HEIGHT = 112

// Changelog cells wrap to two lines instead of cutting the text, which stays readable on hover
const renderWrappedText = ({ cellData }: { cellData?: string | null }): JSX.Element => (
  <span className="text-wrap text-clamp-2" title={cellData ?? ''}>
    {cellData}
  </span>
)

const CHANGELOG_ROW_HEIGHT = 45
const CHANGELOG_HEADER_HEIGHT = 40

// Grows with the rows up to maxHeight, then the table scrolls
const changelogTableHeight = (rows: number, maxHeight: number): number =>
  Math.min(rows * CHANGELOG_ROW_HEIGHT + CHANGELOG_HEADER_HEIGHT, maxHeight)

const SORTABLE_FIELD_KEYS = [
  'id',
  'cardmarket_data_changed_at',
  'full_data_fetched_at',
  'has_image',
  'image_path',
  'should_be_updated',
  'scryfall_id',
  'tcg_player_id',
]

const gameOptions = Object.entries(GAME_NAMES).map(([id, name]) => (
  <MenuItem key={id} value={id} data-testid={`game-option-${id}`}>
    {name}
  </MenuItem>
))

const ExpansionChangelogTable = ({ expansionId }: { expansionId: number }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [changelogs, setChangelogs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetch = async () => {
      try {
        setChangelogs(await getMkmExpansionChangelogs(expansionId))
      } catch {
        enqueueSnackbar('Failed to fetch expansion changelogs', { variant: 'error' })
      }
      setLoading(false)
    }
    fetch()
  }, [expansionId])

  return (
    <>
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : changelogs.length === 0 ? (
        <p className="m-0">No changelog entries</p>
      ) : (
        <div style={{ height: changelogTableHeight(changelogs.length, 400) }}>
          <AutoSizer>
            {({ width, height }) => (
              <Table
                width={width}
                height={height}
                headerHeight={CHANGELOG_HEADER_HEIGHT}
                rowHeight={CHANGELOG_ROW_HEIGHT}
                rowCount={changelogs.length}
                rowGetter={({ index }) => changelogs[index]}
                className="users-table"
              >
                <Column
                  width={100}
                  flexGrow={0}
                  flexShrink={0}
                  label="Date"
                  dataKey="createdAt"
                  cellRenderer={({ cellData }) => (
                    <span className="text-wrap">{getFormattedDate(cellData)}</span>
                  )}
                />
                <Column
                  width={150}
                  flexGrow={1}
                  flexShrink={1}
                  label="Name"
                  dataKey="name"
                  cellRenderer={renderWrappedText}
                />
                <Column
                  width={70}
                  flexGrow={1}
                  flexShrink={1}
                  label="Code"
                  dataKey="code"
                  cellRenderer={renderWrappedText}
                />
                <Column
                  width={120}
                  flexGrow={1}
                  flexShrink={1}
                  label="Name DE"
                  dataKey="nameDe"
                  cellRenderer={renderWrappedText}
                />
                <Column
                  width={120}
                  flexGrow={1}
                  flexShrink={1}
                  label="Name ES"
                  dataKey="nameEs"
                  cellRenderer={renderWrappedText}
                />
                <Column
                  width={120}
                  flexGrow={1}
                  flexShrink={1}
                  label="Name FR"
                  dataKey="nameFr"
                  cellRenderer={renderWrappedText}
                />
                <Column
                  width={120}
                  flexGrow={1}
                  flexShrink={1}
                  label="Name IT"
                  dataKey="nameIt"
                  cellRenderer={renderWrappedText}
                />
                <Column
                  width={80}
                  flexGrow={1}
                  flexShrink={1}
                  label="Released"
                  dataKey="isReleased"
                  cellRenderer={({ cellData }) => (cellData ? 'Yes' : 'No')}
                />
                <Column
                  width={100}
                  flexGrow={0}
                  flexShrink={0}
                  label="Release Date"
                  dataKey="releaseDate"
                  cellRenderer={({ cellData }) =>
                    cellData ? <span className="text-wrap">{getFormattedDate(cellData)}</span> : '-'
                  }
                />
              </Table>
            )}
          </AutoSizer>
        </div>
      )}
    </>
  )
}

const ProductChangelogDialog = ({
  product,
  onClose,
}: {
  product: { id: number; name: string }
  onClose: () => void
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [changelogs, setChangelogs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [expansionChangelogId, setExpansionChangelogId] = useState<number | null>(null)

  useEffect(() => {
    const fetch = async () => {
      try {
        setChangelogs(await getMkmProductChangelogs(product.id))
      } catch {
        enqueueSnackbar('Failed to fetch product changelogs', { variant: 'error' })
      }
      setLoading(false)
    }
    fetch()
  }, [product.id])

  // The expansion changelog replaces the product one in the same dialog, instead of a dialog on top
  if (expansionChangelogId)
    return (
      <ClosableDialog open onClose={onClose} maxWidth="xl" fullWidth>
        {/* pe-5 keeps the title clear of the dialog's close button */}
        <div className="d-flex align-items-center gap-2 mb-3 pe-5">
          <IconButton
            size="small"
            title={`Back to ${product.name}`}
            onClick={() => setExpansionChangelogId(null)}
          >
            <i className="fa-light fa-arrow-left"></i>
          </IconButton>
          <h3 className="m-0">Expansion Changelog (ID: {expansionChangelogId})</h3>
        </div>
        <ExpansionChangelogTable expansionId={expansionChangelogId} />
      </ClosableDialog>
    )

  return (
    <>
      <ClosableDialog open onClose={onClose} maxWidth="xl" fullWidth>
        <h3 className="mb-3 pe-5">
          Product Changelog: {product.name} (ID: {product.id})
        </h3>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : changelogs.length === 0 ? (
          <p className="m-0">No changelog entries</p>
        ) : (
          <div style={{ height: changelogTableHeight(changelogs.length, 500) }}>
            <AutoSizer>
              {({ width, height }) => (
                <Table
                  width={width}
                  height={height}
                  headerHeight={CHANGELOG_HEADER_HEIGHT}
                  rowHeight={CHANGELOG_ROW_HEIGHT}
                  rowCount={changelogs.length}
                  rowGetter={({ index }) => changelogs[index]}
                  className="users-table"
                >
                  <Column
                    width={100}
                    flexGrow={0}
                    flexShrink={0}
                    label="Date"
                    dataKey="createdAt"
                    cellRenderer={({ cellData }) => (
                      <span className="text-wrap">{getFormattedDate(cellData)}</span>
                    )}
                  />
                  <Column
                    width={150}
                    flexGrow={1}
                    flexShrink={1}
                    label="Name"
                    dataKey="name"
                    cellRenderer={renderWrappedText}
                  />
                  <Column
                    width={50}
                    flexGrow={1}
                    flexShrink={1}
                    label="CN"
                    dataKey="cn"
                    cellRenderer={renderWrappedText}
                  />
                  <Column
                    width={90}
                    flexGrow={1}
                    flexShrink={1}
                    label="Rarity"
                    dataKey="rarity"
                    cellRenderer={renderWrappedText}
                  />
                  <Column
                    width={110}
                    flexGrow={1}
                    flexShrink={1}
                    label="Category"
                    dataKey="categoryName"
                    cellRenderer={renderWrappedText}
                  />
                  <Column
                    width={110}
                    flexGrow={1}
                    flexShrink={1}
                    label="Name DE"
                    dataKey="nameDe"
                    cellRenderer={renderWrappedText}
                  />
                  <Column
                    width={110}
                    flexGrow={1}
                    flexShrink={1}
                    label="Name ES"
                    dataKey="nameEs"
                    cellRenderer={renderWrappedText}
                  />
                  <Column
                    width={110}
                    flexGrow={1}
                    flexShrink={1}
                    label="Name FR"
                    dataKey="nameFr"
                    cellRenderer={renderWrappedText}
                  />
                  <Column
                    width={110}
                    flexGrow={1}
                    flexShrink={1}
                    label="Name IT"
                    dataKey="nameIt"
                    cellRenderer={renderWrappedText}
                  />
                  <Column
                    width={110}
                    flexGrow={1}
                    flexShrink={1}
                    label="Website"
                    dataKey="website"
                    cellRenderer={renderWrappedText}
                  />
                  <Column
                    width={100}
                    flexGrow={1}
                    flexShrink={1}
                    label="Expansion"
                    dataKey="expansionId"
                    cellRenderer={({ cellData }) =>
                      cellData ? (
                        <IconButton
                          size="small"
                          title="View expansion changelog"
                          onClick={() => setExpansionChangelogId(cellData)}
                        >
                          <i className="fa-light fa-clock-rotate-left"></i>
                        </IconButton>
                      ) : (
                        '-'
                      )
                    }
                  />
                </Table>
              )}
            </AutoSizer>
          </div>
        )}
      </ClosableDialog>
    </>
  )
}

export const MkmProductsSearch = (): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const expansions = useExpansionData()
  const cardData = useCardData()

  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedGame, setSelectedGame] = useState<string>('')
  const [selectedExpansionId, setSelectedExpansionId] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  // 'all' rather than '' so the filter shows its value, as Sort By does
  const [cardtraderId, setCardtraderId] = useState<'all' | 'with' | 'without'>('all')

  const categories = useMemo(() => {
    const filtered = selectedGame
      ? cardData.filter((c) => c.toPlainObject().idGame === Number(selectedGame))
      : cardData
    return [
      ...new Set(filtered.map((c) => c.categoryName).filter((c): c is string => Boolean(c))),
    ].sort()
  }, [cardData, selectedGame])

  const allExpansionsOption = {
    name: t('addArticles.allExpansions'),
    code: '',
    _id: 0,
  } as Expansion
  const [sortBy, setSortBy] = useState('cardmarket_data_changed_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [products, setProducts] = useState<any[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [changelogProduct, setChangelogProduct] = useState<{ id: number; name: string } | null>(
    null
  )

  const handleSearch = async () => {
    setLoading(true)
    try {
      const params: Parameters<typeof getMkmProducts>[0] = {
        page: currentPage,
        sortBy,
        sortDirection,
      }
      if (searchQuery) params.search = searchQuery
      if (selectedGame) params.idGame = selectedGame
      if (selectedExpansionId) params.expansionId = selectedExpansionId
      if (selectedCategory) params.categoryName = selectedCategory
      if (cardtraderId !== 'all') params.cardtraderId = cardtraderId

      const response = await getMkmProducts(params)
      setProducts(response)
      setHasSearched(true)
    } catch (err) {
      enqueueSnackbar('Failed to fetch MKM products', { variant: 'error' })
    }
    setLoading(false)
  }

  useEffect(() => {
    handleSearch()
  }, [currentPage])

  return (
    <>
      <h3 className="my-4">{t('backOffice.mkmProducts.title')}</h3>
      <Grid container spacing={3} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
          <TextField
            type="text"
            label={t('backOffice.mkmProducts.searchByName')}
            fullWidth
            data-testid="mkm-products-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setCurrentPage(0)
                handleSearch()
              }
            }}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2} lg={2} xl={2}>
          <Filter
            type="select"
            menuitems={[
              <MenuItem key="all" value="" data-testid="game-option-all">
                {t('backOffice.mkmProducts.allGames')}
              </MenuItem>,
              ...gameOptions,
            ]}
            setFilter={(_name, newValue) => {
              setSelectedGame(newValue)
              setSelectedExpansionId('')
              setSelectedCategory('')
            }}
            defaultValue=""
            label={t('backOffice.mkmProducts.game')}
            name="game"
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
          <Box marginTop="11px">
            <ExpansionAutocomplete
              value={expansions.find((e) => String(e._id) === selectedExpansionId)?.code || ''}
              options={[allExpansionsOption]}
              activeGame={
                selectedGame ? getGameMetaData(Number(selectedGame) as IdGame) : undefined
              }
              onChange={(e) => {
                setSelectedExpansionId(e._id ? String(e._id) : '')
                if (e._id) {
                  const exp = expansions.find((ex) => ex._id === e._id)
                  if (exp && String(exp.idGame) !== selectedGame) {
                    setSelectedGame(String(exp.idGame))
                    setSelectedCategory('')
                  }
                }
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
          <Box marginTop="11px">
            <Autocomplete
              options={[t('backOffice.mkmProducts.allCategories'), ...categories]}
              value={selectedCategory || t('backOffice.mkmProducts.allCategories')}
              onChange={(_e, newValue) =>
                setSelectedCategory(
                  newValue === t('backOffice.mkmProducts.allCategories') ? '' : newValue || ''
                )
              }
              getOptionLabel={(option) => option}
              autoHighlight
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="category"
                  margin="dense"
                  type="text"
                  onFocus={(e) => e.target.select()}
                  fullWidth
                />
              )}
            />
          </Box>
        </Grid>

        <Grid item xs={6} sm={3} md={2} lg={2} xl={2}>
          <Filter
            type="select"
            menuitems={[
              <MenuItem key="all" value="all" data-testid="cardtrader-id-all">
                {t('backOffice.mkmProducts.cardtraderIdFilter.all')}
              </MenuItem>,
              <MenuItem key="with" value="with" data-testid="cardtrader-id-with">
                {t('backOffice.mkmProducts.cardtraderIdFilter.with')}
              </MenuItem>,
              <MenuItem key="without" value="without" data-testid="cardtrader-id-without">
                {t('backOffice.mkmProducts.cardtraderIdFilter.without')}
              </MenuItem>,
            ]}
            setFilter={(_name, newValue) => setCardtraderId(newValue)}
            defaultValue={cardtraderId}
            label={t('backOffice.mkmProducts.cardtraderIdFilter.label')}
            name="cardtraderId"
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2} lg={2} xl={2}>
          <Filter
            type="select"
            menuitems={SORTABLE_FIELD_KEYS.map((key) => (
              <MenuItem key={key} value={key} data-testid={`sort-field-${key}`}>
                {t(`backOffice.mkmProducts.sortFields.${key}`)}
              </MenuItem>
            ))}
            setFilter={(_name, newValue) => setSortBy(newValue)}
            defaultValue={sortBy}
            label={t('backOffice.mkmProducts.sortBy')}
            name="sortBy"
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
          <Filter
            type="select"
            menuitems={[
              <MenuItem key="desc" value="desc" data-testid="sort-direction-desc">
                DESC
              </MenuItem>,
              <MenuItem key="asc" value="asc" data-testid="sort-direction-asc">
                ASC
              </MenuItem>,
            ]}
            setFilter={(_name, newValue) => setSortDirection(newValue)}
            defaultValue={sortDirection}
            label={t('backOffice.mkmProducts.direction')}
            name="sortDirection"
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
          <Button
            variant="contained"
            data-testid="search-mkm-products-btn"
            onClick={() => {
              setCurrentPage(0)
              handleSearch()
            }}
          >
            {t('backOffice.mkmProducts.search')}
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <div className="w-100 d-flex flex-column align-items-center">
          <h6 className="pb-5">Loading</h6>
          <CircularProgress size={100} />
        </div>
      ) : products.length === 0 ? (
        hasSearched && (
          <span className="my-5 d-block text-center fw-bold" style={{ fontSize: '1.1rem' }}>
            No search results found
          </span>
        )
      ) : (
        <>
          <div className="transactions-table my-5">
            <span className="mb-1" data-testid="mkm-products-row-count">
              {products.length} rows
            </span>
            {/* Below the table's minimum width this scrolls sideways instead of hiding the last columns */}
            <div className="overflow-auto">
              <AutoSizer disableHeight>
                {({ width }) => (
                  <Table
                    width={Math.max(width, 1500)}
                    // as tall as its rows, up to six of them: a fixed height leaves an empty block below a short list
                    height={Math.min(products.length * ROW_HEIGHT + 40, 6 * ROW_HEIGHT + 40)}
                    headerHeight={40}
                    rowHeight={ROW_HEIGHT}
                    rowCount={products.length}
                    rowGetter={({ index }) => products[index]}
                    className="users-table"
                  >
                    <Column
                      width={80}
                      flexGrow={0}
                      flexShrink={0}
                      label=""
                      dataKey="img"
                      cellRenderer={({ rowData }) => (
                        <ImagePopover
                          variant="imagePopover"
                          imageComponent={
                            <img
                              width="62px"
                              src={getProductImageUrl(rowData.id)}
                              alt={rowData.name}
                            />
                          }
                          imageId={String(rowData.id)}
                        />
                      )}
                    />
                    <Column
                      width={280}
                      flexGrow={2}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.name')}
                      dataKey="name"
                      cellRenderer={({ rowData }) => {
                        const name = [rowData.cn, rowData.name].filter(Boolean).join(' - ')
                        const expansion = [rowData.expansionCode, rowData.expansionName]
                          .filter(Boolean)
                          .join(' - ')
                        return (
                          <div className="d-flex flex-column">
                            <span className="fw-semibold text-wrap text-clamp-2" title={name}>
                              {name}
                            </span>
                            <span className="text-wrap text-clamp-2" title={expansion}>
                              {expansion}
                            </span>
                          </div>
                        )
                      }}
                    />
                    <Column
                      width={160}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.rarity')}
                      dataKey="rarity"
                      cellRenderer={({ rowData }) => (
                        <div className="d-flex flex-column">
                          <span className="text-wrap">{rowData.rarity}</span>
                          <span className="text-wrap text-clamp-2" title={rowData.categoryName}>
                            {rowData.categoryName}
                          </span>
                        </div>
                      )}
                    />
                    <Column
                      width={100}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.hasImage')}
                      dataKey="hasImage"
                      cellRenderer={({ rowData }) => (
                        <>
                          <span>{rowData.hasImage ? 'Yes' : 'No'}</span>
                        </>
                      )}
                    />
                    <Column
                      width={150}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.cardmarketId')}
                      dataKey="id"
                      cellRenderer={({ rowData }) => {
                        const game = gameMetaData.find(({ idGame }) => idGame === rowData.idGame)
                        if (!game) return <span>{rowData.id}</span>
                        const gameName = game.cardmarketName || game.name
                        return (
                          <>
                            <a
                              href={`https://cardmarket.com/en/${gameName}/Products?idProduct=${rowData.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {rowData.id}
                            </a>
                          </>
                        )
                      }}
                    />
                    <Column
                      width={150}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.cardtraderIds')}
                      dataKey="cardtraderHandles"
                      cellRenderer={({ rowData }) => (
                        <>
                          {rowData.cardtraderHandles?.map((handle: string, i: number) => (
                            <span key={handle}>
                              {i > 0 && ', '}
                              <a
                                href={`https://www.cardtrader.com/cards/${handle}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {handle}
                              </a>
                            </span>
                          ))}
                        </>
                      )}
                    />
                    <Column
                      width={130}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.tcgPlayerId')}
                      dataKey="tcgPlayerId"
                      cellRenderer={({ rowData }) => (
                        <>
                          <span>{rowData.tcgPlayerId}</span>
                        </>
                      )}
                    />
                    <Column
                      width={280}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.scryfallId')}
                      dataKey="scryfallId"
                      cellRenderer={({ rowData }) => (
                        <>
                          <span>{rowData.scryfallId}</span>
                        </>
                      )}
                    />
                    <Column
                      width={130}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.shouldUpdate')}
                      dataKey="shouldBeUpdated"
                      cellRenderer={({ rowData }) => (
                        <>
                          <span>{rowData.shouldBeUpdated ? 'Yes' : 'No'}</span>
                        </>
                      )}
                    />
                    <Column
                      width={150}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.dataChangedAt')}
                      dataKey="cardmarketDataChangedAt"
                      cellRenderer={({ rowData }) => (
                        <span className="text-wrap">
                          {getFormattedDate(rowData.cardmarketDataChangedAt)}
                        </span>
                      )}
                    />
                    <Column
                      width={150}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backOffice.mkmProducts.columns.fullDataFetched')}
                      dataKey="fullDataFetchedAt"
                      cellRenderer={({ rowData }) => (
                        <span className="text-wrap">
                          {getFormattedDate(rowData.fullDataFetchedAt)}
                        </span>
                      )}
                    />
                    <Column
                      width={50}
                      flexGrow={0}
                      flexShrink={0}
                      label=""
                      dataKey="changelog"
                      cellRenderer={({ rowData }) => (
                        <>
                          <IconButton
                            size="small"
                            title="View changelog"
                            onClick={() =>
                              setChangelogProduct({ id: rowData.id, name: rowData.name })
                            }
                          >
                            <i className="fa-light fa-clock-rotate-left"></i>
                          </IconButton>
                        </>
                      )}
                    />
                  </Table>
                )}
              </AutoSizer>
            </div>
          </div>
          {(currentPage > 0 || products.length >= PAGE_SIZE) && (
            <TablePaginationControls
              currentPage={currentPage}
              setPage={setCurrentPage}
              isLastPage={products.length < PAGE_SIZE}
              startFromZero={true}
            />
          )}
        </>
      )}
      {changelogProduct && (
        <ProductChangelogDialog
          product={changelogProduct}
          onClose={() => setChangelogProduct(null)}
        />
      )}
    </>
  )
}
